import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { getPolyglot } from '../../../i18n';
import StoerkIdAPI from '../../../redux/stoerkID/StoerkIdAPI';
import useParseErrorMessage from '../../../handlingErrors/useParseErrorMessage';
import CustomersTable from './CustomersTable';
import { getWorkspaceType } from './CustomersTable';
import { useAppDispatch, useAppSelector } from '../../../redux/store.model';
import { getAccountsByWorkspace } from '../../../redux/stoerkID/selectors/StoerkId.selectors';
import CustomersFilters, { CustomersFiltersProps } from './CustomersFilters';
import getAllTextFromRequest from '../util/getAllTextFromRequest';
import UserAPI from '../../../redux/users/api/UserAPI';

function CustomersListPage() {
  const polyglot = getPolyglot();
  const {
    error: errorSalesTeamRequests,
    isLoading: isLoadingSalesTeamRequests,
    isError: isErrorSalesTeamRequests,
  } = StoerkIdAPI.useGetSalesTeamRequestsQuery(undefined);
  const {
    error: errorWorkspaces,
    isLoading: isLoadingWorkspaces,
    isError: isErrorWorkspaces,
  } = StoerkIdAPI.useGetAllWorkspaceQuery(undefined);
  const dispatch = useAppDispatch();

  const error = errorSalesTeamRequests || errorWorkspaces;
  const isError = isErrorSalesTeamRequests || isErrorWorkspaces;
  const isLoading = isLoadingSalesTeamRequests || isLoadingWorkspaces;

  const parseErrorMessage = useParseErrorMessage();

  const groupedRequestByWorkspace = useAppSelector(getAccountsByWorkspace);

  // Derive stable string keys for dispatching (won't change reference on unrelated store updates)
  const workspaceIds = useAppSelector((state) => {
    const grouped = getAccountsByWorkspace(state);
    return (
      grouped
        ?.map((r) => r.workspace?.id)
        .filter(Boolean)
        .sort()
        .join(',') ?? ''
    );
  });
  const userIdsToFetch = useAppSelector((state) => {
    const grouped = getAccountsByWorkspace(state);
    return (
      grouped
        ?.flatMap((r) => [r.workspace?.users[0], r.lastRequest?.creatorId])
        .filter(Boolean)
        .sort()
        .join(',') ?? ''
    );
  });

  // Dispatch usage/subworkspaces queries using stable ID keys
  useEffect(() => {
    if (!workspaceIds) return;
    workspaceIds.split(',').forEach((id) => {
      dispatch(StoerkIdAPI.endpoints.getWorkspaceUsage.initiate(id));
      dispatch(StoerkIdAPI.endpoints.getSubWorkspaces.initiate(id));
    });
  }, [dispatch, workspaceIds]);

  // Dispatch user queries using stable ID keys
  useEffect(() => {
    if (!userIdsToFetch) return;
    userIdsToFetch.split(',').forEach((id) => {
      dispatch(UserAPI.endpoints.getUser.initiate(id));
    });
  }, [dispatch, userIdsToFetch]);
  /** filters */
  const [valueTab, setValueTab] =
    React.useState<CustomersFiltersProps['valueTab']>('ALL');
  const [search, setSearch] = React.useState<string>('');
  const handleChangeTabFilter = (
    event: React.SyntheticEvent,
    newValue: CustomersFiltersProps['valueTab']
  ) => {
    setValueTab(newValue);
  };
  const handleChangeSearchFilter = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearch(event.target.value);
  };
  /* filter by text and by tab */
  const subworkspacesDicSerialized = useAppSelector((state) => {
    const dic: Record<string, any> = {};
    groupedRequestByWorkspace?.forEach((request) => {
      const workspaceId = request.workspace?.id;
      if (workspaceId) {
        dic[workspaceId] =
          StoerkIdAPI.endpoints.getSubWorkspaces.select(workspaceId)(
            state
          ).data;
      }
    });
    return JSON.stringify(dic);
  });
  const subworkspacesDic: Record<string, any> = useMemo(
    () => JSON.parse(subworkspacesDicSerialized),
    [subworkspacesDicSerialized]
  );

  // Dispatch child workspace usage queries
  const childIdsCsv = useMemo(() => {
    const childIds: string[] = [];
    Object.values(subworkspacesDic).forEach((subData: any) => {
      if (subData) {
        childIds.push(...Object.keys(subData));
      }
    });
    return childIds.sort().join(',');
  }, [subworkspacesDic]);

  useEffect(() => {
    if (!childIdsCsv) return;
    childIdsCsv.split(',').forEach((childId) => {
      dispatch(StoerkIdAPI.endpoints.getWorkspaceUsage.initiate(childId));
    });
  }, [dispatch, childIdsCsv]);

  const itemsSorted = React.useMemo(() => {
    if (!groupedRequestByWorkspace) return [];
    return groupedRequestByWorkspace
      .filter((request) => {
        if (search === '') return true;

        const searchLowerCase = search.toLowerCase();
        const textFromRequest = getAllTextFromRequest(request).toLowerCase();
        return textFromRequest.includes(searchLowerCase);
      })
      .filter((request) => {
        switch (valueTab) {
          case 'ALL':
            return true;
          case 'ORGANIZATION': {
            const type = getWorkspaceType(
              request.workspace?.id,
              subworkspacesDic[request.workspace?.id ?? '']
            );
            return type === 'organization';
          }
          case 'SIMPLE': {
            const type = getWorkspaceType(
              request.workspace?.id,
              subworkspacesDic[request.workspace?.id ?? '']
            );
            return type === 'simple';
          }
          case 'IN_PROGRESS':
            return [
              'PAYMENT_FAILED',
              'PENDING_STAFF',
              'PENDING_PAYMENT',
              'PAYMENT_FAILED',
              'USER_ACCEPT',
            ].includes(request.lastRequest?.requestStatus as any);
          case 'COMPLETED':
            return request.lastRequest?.requestStatus === 'ACTIVE';
          case 'CANCELLED':
            return ['REJECTED', 'CANCELLED', 'USER_ACCEPT'].includes(
              request.lastRequest?.requestStatus as any
            );
          default:
            return true;
        }
      });
  }, [groupedRequestByWorkspace, search, valueTab, subworkspacesDic]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        padding: 3,
        paddingBottom: 2,
      }}
      disableGutters
    >
      <Box mb={1}>
        <Typography variant="h1">
          {polyglot.t('stoerk_id.customer_list.title')}
        </Typography>
        <Typography>{polyglot.t('stoerk_id.customer_list.note')}</Typography>
      </Box>

      {isLoading && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100px"
        >
          <CircularProgress />
        </Box>
      )}

      {isError && <Alert severity="error">{parseErrorMessage(error)}</Alert>}

      {groupedRequestByWorkspace && !isLoading && !isError && (
        <>
          <Box sx={{ mt: 1 }}>
            <CustomersFilters
              valueTab={valueTab}
              onChangeTab={handleChangeTabFilter}
              onChangeSearch={handleChangeSearchFilter}
            />
          </Box>

          <CustomersTable items={itemsSorted} />
        </>
      )}
    </Container>
  );
}

export default CustomersListPage;
